import type { MaybeRefOrGetter } from 'vue';
import type { UsercentricsServiceKey } from './usercentricsServices';
import { computed, toValue } from 'vue';
import log from '~/utils/log';
import { getUsercentricsServiceId } from './usercentricsServices';

interface UsercentricsConsentService {
    id: string;
    templateId?: string;
    consent?: {
        status?: boolean;
    };
}

let cacheRefreshPromise: Promise<void> | null = null;
let consentUpdatePromise: Promise<void> | null = null;

const consentUpdateEvents = ['UC_UI_INITIALIZED', 'UC_CONSENT', 'UC_UI_CMP_EVENT'] as const;
const usercentricsUiWaitTimeout = 5000;
const usercentricsConsentRefreshTimeout = 5000;

const toConsentServicesSnapshot = (services: ReadonlyArray<UsercentricsService>): UsercentricsConsentService[] => {
    return services.map((service) => {
        const snapshot: UsercentricsConsentService = { id: service.id };

        if (service.templateId !== undefined) snapshot.templateId = service.templateId;
        if (service.consent?.status !== undefined) snapshot.consent = { status: service.consent.status };

        return snapshot;
    });
};

const useUsercentricsServicesState = () => {
    return useState<UsercentricsConsentService[] | null>('usercentrics-consent-services', () => null);
};

const getConsentFromServices = (
    services: ReadonlyArray<UsercentricsConsentService> | null,
    key: UsercentricsServiceKey
): boolean | null => {
    const serviceId = getUsercentricsServiceId(key);
    const service = services?.find((item) => item.id === serviceId || item.templateId === serviceId);

    return service?.consent?.status ?? null;
};

const waitForUsercentricsState = () => {
    // Usercentrics emits consent events before getServicesBaseInfo() reliably returns the updated state.
    // Waiting one frame plus one microtask lets the internal Usercentrics state settle first.
    return new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => {
            window.queueMicrotask(resolve);
        });
    });
};

const getServicesBaseInfoWithTimeout = (usercentricsUi: NonNullable<Window['UC_UI']>) => {
    return new Promise<Awaited<ReturnType<typeof usercentricsUi.getServicesBaseInfo>>>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
            reject(new Error('Usercentrics consent state refresh timed out'));
        }, usercentricsConsentRefreshTimeout);

        Promise.resolve(usercentricsUi.getServicesBaseInfo())
            .then(resolve, reject)
            .finally(() => {
                window.clearTimeout(timeout);
            });
    });
};

/**
 * Refreshes the shared Usercentrics consent cache from the current Usercentrics UI state.
 * This needs to be a promise, because getServicesBaseInfo can be async.
 * While a refresh is already running, all callers reuse the same promise so we only read Usercentrics once.
 */
export const refreshUsercentricsConsentCache = (): Promise<void> => {
    if (!import.meta.client || !window.UC_UI) return Promise.resolve();

    if (cacheRefreshPromise) return cacheRefreshPromise;

    const usercentricsUi = window.UC_UI;
    const servicesState = useUsercentricsServicesState();

    cacheRefreshPromise = Promise.resolve()
        .then(() => getServicesBaseInfoWithTimeout(usercentricsUi))
        .then((services) => {
            servicesState.value = toConsentServicesSnapshot(services);
        })
        .catch((error) => {
            servicesState.value = null;
            throw error;
        })
        .finally(() => {
            cacheRefreshPromise = null;
        });

    return cacheRefreshPromise;
};

const refreshUsercentricsConsentCacheAfterEvent = () => {
    // Usercentrics may emit multiple consent events for the same change. Keep one shared event refresh
    // in flight so subscribers see one settled cache update instead of racing duplicate reads.
    consentUpdatePromise ??= waitForUsercentricsState()
        .then(refreshUsercentricsConsentCache)
        .finally(() => {
            consentUpdatePromise = null;
        });

    return consentUpdatePromise;
};

const showUsercentricsSecondLayer = (): void => {
    Promise.resolve(window.UC_UI?.showSecondLayer()).catch(() => {});
};

/**
 * Waits for Usercentrics to finish loading its functionality after a page load
 */
const waitForUsercentricsUi = (): Promise<NonNullable<Window['UC_UI']> | null> => {
    if (window.UC_UI) return Promise.resolve(window.UC_UI);

    return new Promise((resolve) => {
        const timeout = window.setTimeout(() => resolve(null), usercentricsUiWaitTimeout);

        window.addEventListener(
            'UC_UI_INITIALIZED',
            () => {
                window.clearTimeout(timeout);
                resolve(window.UC_UI ?? null);
            },
            { once: true }
        );
    });
};

const getUsercentricsServiceTemplateId = async (
    usercentricsUi: NonNullable<Window['UC_UI']>,
    key: UsercentricsServiceKey
): Promise<string | null> => {
    const configuredServiceId = getUsercentricsServiceId(key);
    const services = await usercentricsUi.getServicesBaseInfo();
    const service = services.find((item) => item.id === configuredServiceId || item.templateId === configuredServiceId);

    return service?.templateId ?? service?.id ?? null;
};

/**
 * Returns the reactive consent status for a configured Usercentrics service key.
 */
export const useUsercentricsConsentItem = (key: MaybeRefOrGetter<UsercentricsServiceKey>) => {
    const servicesState = useUsercentricsServicesState();

    return computed(() => getConsentFromServices(servicesState.value, toValue(key)));
};

export const openConsentManager = (): void => {
    if (!import.meta.client) return;

    if (window.UC_UI) {
        showUsercentricsSecondLayer();
        return;
    }

    window.addEventListener('UC_UI_INITIALIZED', showUsercentricsSecondLayer, { once: true });
};

/**
 * Accepts consent for a single configured service.
 * The Browser UI API expects the service template id, while our local mapping can contain either id.
 */
export const acceptUsercentricsConsent = async (key: UsercentricsServiceKey): Promise<boolean> => {
    if (!import.meta.client) return false;

    const usercentricsUi = await waitForUsercentricsUi();

    if (!usercentricsUi?.acceptService) return false;

    const serviceId = await getUsercentricsServiceTemplateId(usercentricsUi, key);

    if (!serviceId) return false;

    await usercentricsUi.acceptService(serviceId);
    await waitForUsercentricsState();
    await refreshUsercentricsConsentCache();

    return true;
};

/**
 * Registers one callback for Usercentrics consent changes.
 *
 * Usercentrics emits several events for a single interaction and emits them before getServicesBaseInfo()
 * reliably returns the updated status. The listener waits for the shared event refresh promise
 * before running the callback. If another event arrives while that refresh is in flight, it is ignored;
 * the pending refresh already covers the latest Usercentrics state.
 *
 * Returns an unsubscribe function for component/plugin cleanup.
 */
export const onConsentUpdated = (callback?: () => void): (() => void) => {
    if (!import.meta.client) return () => {};

    let callbackUpdatePromise: Promise<void> | null = null;

    const listener = () => {
        if (callbackUpdatePromise) return;

        callbackUpdatePromise = refreshUsercentricsConsentCacheAfterEvent()
            .catch((error) => {
                log.warn('Failed to refresh Usercentrics consent state', error);
            })
            .then(() => {
                callback?.();
            })
            .finally(() => {
                callbackUpdatePromise = null;
            });
    };

    consentUpdateEvents.forEach((eventName) => {
        window.addEventListener(eventName, listener);
    });

    return () => {
        consentUpdateEvents.forEach((eventName) => {
            window.removeEventListener(eventName, listener);
        });
    };
};
